import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { getContractByToken, signContractByToken } from "@/lib/contracts.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import type { ContractLang } from "@/lib/contract-i18n";

function resolveLang(raw: unknown): ContractLang {
  const s = String(raw ?? "nl").toLowerCase().slice(0, 2);
  return s === "fr" || s === "en" || s === "de" ? s : "nl";
}

const T: Record<ContractLang, Record<string, string>> = {
  nl: {
    pageTitle: "Huurovereenkomst Casa Mi Pajobe",
    loading: "Laden…",
    notFound: "Contract niet gevonden of niet meer geldig.",
    signed: "Ondertekend",
    cancelled: "Geannuleerd",
    waiting: "Wacht op handtekening",
    docTitle: "Volledige huurovereenkomst",
    docHint: "Lees de volledige overeenkomst hieronder. U kunt ze ook downloaden.",
    download: "Download PDF",
    openDoc: "Openen in nieuw venster",
    downloadSigned: "Ondertekend contract downloaden",
    signHeading: "Online ondertekenen",
    fullName: "Volledige naam (typen)",
    nameErr: "Vul uw volledige naam in (minstens 2 tekens).",
    sigLabel: "Handtekening (tekenen met muis of vinger)",
    sigErr: "Plaats hier uw handtekening.",
    clear: "Wissen",
    agree: "Ik bevestig 'gelezen en goedgekeurd' en aanvaard de huurovereenkomst.",
    agreeErr: "U dient akkoord te gaan om te kunnen ondertekenen.",
    todo: "Nog te doen voor u kunt ondertekenen:",
    todoName: "Vul uw volledige naam in",
    todoSig: "Plaats een handtekening",
    todoAgree: "Vink 'gelezen en goedgekeurd' aan",
    submit: "Ondertekenen",
    busy: "Bezig…",
    ok: "Bedankt! Het contract is ondertekend.",
    fill: "Vul alle verplichte velden in",
    alreadySigned: "✓ Reeds ondertekend",
    by: "Door",
    cancelledMsg: "Dit contract is geannuleerd. Neem contact op met de verhuurder.",
  },
  fr: {
    pageTitle: "Contrat de location Casa Mi Pajobe",
    loading: "Chargement…",
    notFound: "Contrat introuvable ou expiré.",
    signed: "Signé",
    cancelled: "Annulé",
    waiting: "En attente de signature",
    docTitle: "Contrat de location complet",
    docHint: "Lisez le contrat complet ci-dessous. Vous pouvez aussi le télécharger.",
    download: "Télécharger le PDF",
    openDoc: "Ouvrir dans un nouvel onglet",
    downloadSigned: "Télécharger le contrat signé",
    signHeading: "Signer en ligne",
    fullName: "Nom complet (à taper)",
    nameErr: "Indiquez votre nom complet (au moins 2 caractères).",
    sigLabel: "Signature (souris ou doigt)",
    sigErr: "Apposez votre signature ici.",
    clear: "Effacer",
    agree: "Je confirme « lu et approuvé » et j'accepte le contrat de location.",
    agreeErr: "Vous devez accepter pour pouvoir signer.",
    todo: "À compléter avant de signer :",
    todoName: "Indiquez votre nom complet",
    todoSig: "Apposez une signature",
    todoAgree: "Cochez « lu et approuvé »",
    submit: "Signer",
    busy: "En cours…",
    ok: "Merci ! Le contrat est signé.",
    fill: "Complétez tous les champs obligatoires",
    alreadySigned: "✓ Déjà signé",
    by: "Par",
    cancelledMsg: "Ce contrat a été annulé. Veuillez contacter le propriétaire.",
  },
  en: {
    pageTitle: "Rental agreement Casa Mi Pajobe",
    loading: "Loading…",
    notFound: "Contract not found or no longer valid.",
    signed: "Signed",
    cancelled: "Cancelled",
    waiting: "Awaiting signature",
    docTitle: "Full rental agreement",
    docHint: "Read the full agreement below. You can also download it.",
    download: "Download PDF",
    openDoc: "Open in new tab",
    downloadSigned: "Download signed contract",
    signHeading: "Sign online",
    fullName: "Full name (typed)",
    nameErr: "Enter your full name (at least 2 characters).",
    sigLabel: "Signature (draw with mouse or finger)",
    sigErr: "Please place your signature here.",
    clear: "Clear",
    agree: "I confirm 'read and approved' and accept the rental agreement.",
    agreeErr: "You must agree in order to sign.",
    todo: "To do before you can sign:",
    todoName: "Enter your full name",
    todoSig: "Place a signature",
    todoAgree: "Tick 'read and approved'",
    submit: "Sign",
    busy: "Working…",
    ok: "Thank you! The contract has been signed.",
    fill: "Please complete all required fields",
    alreadySigned: "✓ Already signed",
    by: "By",
    cancelledMsg: "This contract was cancelled. Please contact the owner.",
  },
  de: {
    pageTitle: "Mietvertrag Casa Mi Pajobe",
    loading: "Laden…",
    notFound: "Vertrag nicht gefunden oder nicht mehr gültig.",
    signed: "Unterschrieben",
    cancelled: "Storniert",
    waiting: "Wartet auf Unterschrift",
    docTitle: "Vollständiger Mietvertrag",
    docHint: "Lesen Sie den vollständigen Vertrag unten. Sie können ihn auch herunterladen.",
    download: "PDF herunterladen",
    openDoc: "In neuem Tab öffnen",
    downloadSigned: "Unterschriebenen Vertrag herunterladen",
    signHeading: "Online unterschreiben",
    fullName: "Vollständiger Name (tippen)",
    nameErr: "Geben Sie Ihren vollständigen Namen ein (mind. 2 Zeichen).",
    sigLabel: "Unterschrift (mit Maus oder Finger zeichnen)",
    sigErr: "Bitte hier unterschreiben.",
    clear: "Löschen",
    agree: "Ich bestätige 'gelesen und genehmigt' und akzeptiere den Mietvertrag.",
    agreeErr: "Sie müssen zustimmen, um unterschreiben zu können.",
    todo: "Vor dem Unterschreiben zu erledigen:",
    todoName: "Vollständigen Namen eingeben",
    todoSig: "Unterschrift setzen",
    todoAgree: "'Gelesen und genehmigt' ankreuzen",
    submit: "Unterschreiben",
    busy: "Läuft…",
    ok: "Danke! Der Vertrag wurde unterschrieben.",
    fill: "Bitte alle Pflichtfelder ausfüllen",
    alreadySigned: "✓ Bereits unterschrieben",
    by: "Von",
    cancelledMsg: "Dieser Vertrag wurde storniert. Bitte kontaktieren Sie den Vermieter.",
  },
};

export const Route = createFileRoute("/contract/$token")({
  component: ContractSignPage,
  head: () => ({ meta: [{ title: "Huurovereenkomst ondertekenen — Casa Mi Pajobe" }] }),
});

function ContractSignPage() {
  const { token } = useParams({ from: "/contract/$token" });
  const fetchByToken = useServerFn(getContractByToken);
  const submitSign = useServerFn(signContractByToken);

  const q = useQuery({
    queryKey: ["contract", token],
    queryFn: () => fetchByToken({ data: { token } }),
    retry: false,
  });

  const [typedName, setTypedName] = useState("");
  const lang = resolveLang((q.data?.contract_data as Record<string, unknown> | undefined)?.locale);
  const t = T[lang];
  const [agreed, setAgreed] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    if (q.data?.renter_name && !typedName) setTypedName(q.data.renter_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.renter_name]);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }
  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0b1320";
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  }
  function end() {
    drawingRef.current = false;
  }
  function clearSig() {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d")!.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  }

  const nameValid = typedName.trim().length >= 2;
  const sigValid = hasInk;
  const agreedValid = agreed;
  const canSubmit = nameValid && sigValid && agreedValid;

  const mut = useMutation({
    mutationFn: async () => {
      if (!canSubmit) throw new Error("Vul alle verplichte velden in");
      const dataUrl = canvasRef.current!.toDataURL("image/png");
      await submitSign({
        data: { token, typed_name: typedName.trim(), signature_data_url: dataUrl, agreed: true },
      });
    },
    onSuccess: () => {
      toast.success(t.ok);
      q.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function handleSubmit() {
    setShowErrors(true);
    if (!canSubmit) {
      toast.error(t.fill);
      return;
    }
    mut.mutate();
  }

  if (q.isLoading) return <div className="p-10 text-center">{t.loading}</div>;
  if (q.error || !q.data)
    return <div className="p-10 text-center text-destructive">{t.notFound}</div>;

  const c = q.data;
  const d = c.contract_data ?? {};
  const isSigned = c.status === "signed";
  const isCancelled = c.status === "cancelled";
  const pdfUrl = `/api/public/contract-pdf/${token}`;

  return (
    <div className="min-h-screen bg-muted/30 py-8">
      <div className="mx-auto max-w-3xl space-y-6 px-4">
        <header className="rounded-lg border bg-card p-6">
          <h1 className="text-xl font-semibold">{t.pageTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {d.renter_first_name} {d.renter_family_name} · {d.check_in} → {d.check_out}
          </p>
          <p className="mt-2 text-xs">
            Status:{" "}
            <span className="font-medium">
              {isSigned ? t.signed : isCancelled ? t.cancelled : t.waiting}
            </span>
            {c.signed_at && (
              <span className="text-muted-foreground"> · {new Date(c.signed_at).toLocaleString("nl-BE")}</span>
            )}
          </p>
        </header>

        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-medium">{t.docTitle}</h2>
              <p className="text-xs text-muted-foreground">{t.docHint}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                  {t.openDoc}
                </a>
              </Button>
              <Button asChild size="sm">
                <a href={`${pdfUrl}?download=1`} download>
                  {isSigned ? t.downloadSigned : t.download}
                </a>
              </Button>
            </div>
          </div>
          <iframe
            title={t.docTitle}
            src={pdfUrl}
            className="h-[70vh] min-h-[420px] w-full rounded-lg border bg-white"
          />
        </div>

        {isSigned ? (
          <div className="rounded-lg border bg-card p-6">
            <p className="font-medium text-emerald-600">{t.alreadySigned}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t.by}: {c.signed_typed_name}
            </p>
            {c.signature_data_url && (
              <img src={c.signature_data_url} alt="Handtekening" className="mt-3 h-24 rounded border bg-white" />
            )}
            <Button asChild size="sm" className="mt-4">
              <a href={`${pdfUrl}?download=1`} download>
                {t.downloadSigned}
              </a>
            </Button>
          </div>
        ) : isCancelled ? (
          <div className="rounded-lg border bg-card p-6 text-destructive">
            {t.cancelledMsg}
          </div>
        ) : (
          <div className="rounded-lg border bg-card p-6 space-y-4">
            <h2 className="font-medium">{t.signHeading}</h2>
            <div>
              <Label className="text-xs">
                {t.fullName} <span className="text-destructive">*</span>
              </Label>
              <Input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                aria-invalid={showErrors && !nameValid}
                className={`mt-1 ${showErrors && !nameValid ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              {showErrors && !nameValid && <p className="mt-1 text-xs text-destructive">{t.nameErr}</p>}
            </div>
            <div>
              <Label className="text-xs">
                {t.sigLabel} <span className="text-destructive">*</span>
              </Label>
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={end}
                onPointerLeave={end}
                aria-invalid={showErrors && !sigValid}
                className={`mt-1 w-full touch-none rounded border bg-white ${showErrors && !sigValid ? "border-destructive ring-1 ring-destructive" : ""}`}
              />
              <div className="mt-1 flex items-center justify-between">
                {showErrors && !sigValid ? (
                  <p className="text-xs text-destructive">{t.sigErr}</p>
                ) : (
                  <span />
                )}
                <Button variant="ghost" size="sm" onClick={clearSig} type="button">
                  {t.clear}
                </Button>
              </div>
            </div>
            <div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(Boolean(v))} />
                <span>
                  {t.agree} <span className="text-destructive">*</span>
                </span>
              </label>
              {showErrors && !agreedValid && <p className="mt-1 text-xs text-destructive">{t.agreeErr}</p>}
            </div>

            {!canSubmit && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-100">
                <p className="font-medium">{t.todo}</p>
                <ul className="mt-1 list-disc pl-5">
                  {!nameValid && <li>{t.todoName}</li>}
                  {!sigValid && <li>{t.todoSig}</li>}
                  {!agreedValid && <li>{t.todoAgree}</li>}
                </ul>
              </div>
            )}

            <Button
              onClick={handleSubmit}
              disabled={mut.isPending || !canSubmit}
              className="w-full"
              aria-disabled={!canSubmit}
            >
              {mut.isPending ? t.busy : t.submit}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
